<template>
    <CardFront>
        <div class="tw:flex tw:items-start tw:gap-9 tw:flex-wrap tw:md:flex-nowrap">
            <div class="tw:flex tw:items-start tw:gap-3 tw:min-w-10">
                <div class="tw:w-10 tw:h-10 tw:rounded-lg tw:bg-slate-200 tw:flex tw:items-center tw:justify-center tw:shrink-0 tw:mt-0.5">
                    <i class="material-icons tw:text-slate-500 tw:text-base">local_activity</i>
                </div>
            </div>

            <div v-if="entradaLocal.user_nombre">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0">{{ $t('perfil.entrada_detalle.titular') }}</p>
                <p class="tw:text-sm tw:font-semibold tw:text-slate-900 tw:m-0 tw:leading-tight">
                    {{ entradaLocal.user_nombre }} <span>{{ entradaLocal.user_apellidos }}</span>
                </p>
                <p class="tw:text-xs tw:text-slate-500 tw:font-mono tw:m-0">{{ entradaLocal.user_dni }}</p>
            </div>

            <div v-if="entradaLocal.qr_code">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0">{{ $t('perfil.entrada_detalle.qr_code') }}</p>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <p class="tw:text-sm tw:font-semibold tw:text-slate-900 tw:m-0 tw:leading-tight">
                        {{ entradaLocal.qr_code }}
                    </p>
                    <QrPreview v-if="entradaLocal.qr_code" :value="entradaLocal.qr_code" />
                </div>
            </div>

            <div v-if="entradaLocal.asiento || entradaLocal.plaza">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0">{{ $t('perfil.entrada_detalle.ubicacion') }}</p>
                <p class="tw:text-sm tw:font-semibold tw:text-slate-900 tw:m-0">{{ ubicacionLabel }}</p>
                <p class="tw:text-[10px] tw:text-slate-500 tw:font-bold tw:uppercase tw:m-0">
                    {{ $t('perfil.entrada_detalle.fila_asiento', { fila: filaLabel, asiento: butacaLabel }) }}
                </p>
            </div>

            <div>
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0 tw:mb-1">{{ $t('perfil.entrada_detalle.enviado') }}</p>
                <div class="tw:flex tw:items-center tw:gap-1">
                    <BadgeFront :label="estaEnviada ? $t('perfil.si_enviado') : $t('perfil.no_enviado')" :variant="enviadoVariant" custom-class="tw:w-14 tw:justify-center" />
                    <PopoverFront v-if="estaEnviada" position="top">
                        <template #trigger>
                            <i class="material-icons tw:text-sm tw:text-slate-400 tw:hover:text-slate-600 tw:cursor-pointer">info</i>
                        </template>
                        <div class="tw:space-y-1 tw:pr-4">
                            <p v-if="entradaLocal.enviado.email" class="tw:m-0 tw:flex tw:items-center tw:gap-1">
                                <i class="material-icons tw:text-xs tw:text-slate-400">email</i>
                                <span>{{ entradaLocal.enviado.email }}</span>
                            </p>
                            <p v-if="entradaLocal.enviado.telefono" class="tw:m-0 tw:flex tw:items-center tw:gap-1">
                                <i class="material-icons tw:text-xs tw:text-slate-400">phone</i>
                                <span>{{ entradaLocal.enviado.telefono }}</span>
                            </p>
                            <p v-if="entradaLocal.enviado.fecha" class="tw:m-0 tw:flex tw:items-center tw:gap-1">
                                <i class="material-icons tw:text-xs tw:text-slate-400">calendar_today</i>
                                <span>{{ entradaLocal.enviado.fecha }}</span>
                            </p>
                        </div>
                    </PopoverFront>
                </div>
            </div>

            <div>
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0 tw:mb-1">{{ $t('perfil.entrada_detalle.anulada') }}</p>
                <BadgeFront :label="estaAnulada ? $t('perfil.si_enviado') : $t('perfil.no_enviado')" :variant="anuladaVariant" custom-class="tw:w-14 tw:justify-center" />
            </div>
        </div>

        <template v-if="!estaAnulada" #footer>
            <div class="tw:flex tw:gap-2 tw:pt-2 tw:border-t tw:border-slate-100">
                <Button variant="default-tiny" as="a" :href="entradaLocal.url_pdf" target="_blank" rel="noopener">
                    <i class="material-icons tw:text-sm">download</i> {{ $t('perfil.entrada_detalle.descargar') }}
                </Button>
                <Button
                    v-if="entradaLocal.puede_cambiar_datos"
                    variant="default-tiny"
                    @click="showEditModal = true"
                >
                    <i class="material-icons tw:text-sm">edit</i> {{ $t('perfil.cambiar_datos') }}
                </Button>
            </div>
            <EditTicketModal
                :show="showEditModal"
                :entrada="entradaLocal"
                :pedido-id="pedido.id"
                @close="showEditModal = false"
                @success="onEditSuccess"
            />
        </template>
    </CardFront>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import PopoverFront from '@/modules/front/ui/PopoverFront.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import EditTicketModal from './EditTicketModal.vue';
    import QrPreview from '@/modules/ui/QrPreview.vue';

    const props = defineProps({
        entrada: { type: Object, required: true },
        pedido:  { type: Object, required: true },
    });

    const entradaLocal = ref({ ...props.entrada });

    const emit = defineEmits(['edit-success']);

    const showEditModal = ref(false);

    const onEditSuccess = ({ nombre, apellidos, dni }) => {
        entradaLocal.value.user_nombre    = nombre;
        entradaLocal.value.user_apellidos = apellidos;
        entradaLocal.value.user_dni       = dni;
        showEditModal.value = false;
        emit('edit-success');
    };

    const estaEnviada    = computed(() => !!(entradaLocal.value.enviado && entradaLocal.value.enviado.email));
    const estaAnulada    = computed(() => !!entradaLocal.value.deleted_at);
    const enviadoVariant = computed(() => estaEnviada.value ? 'success' : 'default');
    const anuladaVariant = computed(() => estaAnulada.value ? 'danger' : 'default');

    const asientoSrc = computed(() => entradaLocal.value.asiento ?? entradaLocal.value.plaza);
    const ubicacionLabel = computed(() => asientoSrc.value?.region?.nombre ?? asientoSrc.value?.zona ?? '—');
    const filaLabel = computed(() => asientoSrc.value?.row ?? asientoSrc.value?.fila ?? '—');
    const butacaLabel = computed(() => asientoSrc.value?.seat ?? asientoSrc.value?.butaca ?? asientoSrc.value?.asiento ?? '—');
</script>
